import { useMemo, useState, FormEventHandler, useCallback } from 'react'

import { ArticleResult } from 'interface/article'
import {
  InterestResult, InterestFormData,
  usePostInterest, useEditInterest, useDeleteInterest
} from 'interface/author/interest'

import {
  Box, Stack, Dialog, DialogTitle, DialogContent, DialogActions, FormGroup, FormControl, FormControlLabel,
  Typography, TextField, Rating, Tooltip, Checkbox, Button, IconButton
} from "@mui/material"

import { Section } from 'components/controls/section'
import { Loading } from "components/controls/loading"
import { ErrorInfo } from "components/controls/errorInfo"
import { EditRefAreticleList } from '../../components/editRefArticleList'

import { CloseIcon, OkIcon, TrushIcon } from "shared/icons"

const articleScoreReasons: {
  score: number
  reason: string
}[] = [
  { score: 17, reason: '著者順位1位かつ3年以内の新しいPubmed論文複数が根拠に含まれる' },
  { score: 16, reason: '著者順位1位かつ3年以内の新しいPubmed論文＋和文論文複数が根拠に含まれる' },
  { score: 15, reason: '著者順位1位かつ5年以内の新しいPubmed論文複数が根拠に含まれる' },
  { score: 14, reason: '著者順位1位かつ3年以内の新しいPubmed論文1件が根拠に含まれる' },
  { score: 13, reason: '著者順位1位かつ5年以内の新しいPubmed論文＋和文論文複数が根拠に含まれる' },
  { score: 12, reason: '著者順位1位かつ3年以内の新しい和文論文1件が根拠に含まれる' },
  { score: 11, reason: '著者順位1位かつ5年以内の新しいPubmed論文1件が根拠に含まれる' },
  { score: 10, reason: '著者順位1位かつ5年以内の新しい和文論文1件が根拠に含まれる' },
  { score: 8, reason: '著者順位1位かつ5年より前の古いPubmed論文＋和文論文複数が根拠に含まれる' },
  { score: 7, reason: '著者順位1位かつ5年より前の古いPubmed論文＋和文論文複数1件が根拠に含まれる' },
  { score: 6, reason: '著者順位2位以下かつ3年以内の新しいPubmed論文＋和文論文複数が根拠に含まれる' },
  { score: 5, reason: '著者順位2位以下かつ3年より前の古いPubmed論文＋和文論文複数が根拠に含まれる' },
  { score: 4, reason: '著者順位2位以下かつ3年以内の新しいPubmed論文＋和文論文1件が根拠に含まれる' },
  { score: 3, reason: '著者順位2位以下かつ3年より前の古いPubmed論文＋和文論文1件が根拠に含まれる' },
  { score: 2, reason: '推定根拠となるPubmed論文・和文論文がない' },
  { score: 1, reason: '推定不能、提案不能' }
]

type EditInterestDialogProps = {
  searchId: number
  authorId: number
  data: InterestResult | null
  articles: ArticleResult[]
  onSave: (data: InterestResult) => void
  onDelete: () => void
  onClose: () => void
}
export const EditInterestDialog = ({
  searchId,
  authorId,
  data,
  articles,
  onSave,
  onDelete,
  onClose
}: EditInterestDialogProps) => {
  const [title, setTitle] = useState<string>(data?.title ?? '')
  const [reason, setReason] = useState<string>(data?.reason ?? '')
  const [articleScore, setArticleScore] = useState<number>(data?.articleScore ?? 0)
  const [ownFlag, setOwnFlag] = useState<boolean>(data?.ownFlag ?? false)
  const [priorityContentFlag, setPriorityContentFlag] = useState<boolean>(data?.priorityContentFlag ?? false)
  const [othersFlag, setOthersFlag] = useState<boolean>(data?.othersFlag ?? false)
  const [refArticleIdList, setRefArticleIdList] = useState<number[]>(data?.ref.map((r) => r.articleId) ?? [])

  const articleScoreReason = useMemo<string>(() => articleScoreReasons.find((r) => r.score === articleScore)?.reason ?? '', [articleScore])

  const [postState, postInterest] = usePostInterest(searchId, authorId, { onPosted: onSave })
  const [editState, editInterest] = useEditInterest(searchId, authorId, { onEditted: onSave })
  const [deleteState, deleteInterest] = useDeleteInterest(searchId, authorId, { onDeleted: onDelete })

  const onSubmitForm: FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault()
    const form: InterestFormData = {
      title: title,
      reason: reason,
      articleScore: articleScore,
      ownFlag: ownFlag,
      priorityContentFlag: priorityContentFlag,
      othersFlag: othersFlag,
      ref: refArticleIdList,
      updateTime: data?.updateTime
    }
    if (data) {
      editInterest({ interestId: data.interestId, form })
    } else {
      postInterest(form)
    }
  }
  const deleteLine = useCallback(
    () => {
      if (!data) return
      deleteInterest(data.interestId)
    },
    [data, deleteInterest]
  )
  return (
    <Dialog open fullWidth maxWidth='md'
      onClose={() => {
        if (postState.posting || editState.posting || deleteState.posting) return
        onClose()
      }}
    >
      { postState.posting || editState.posting || deleteState.posting && <Loading/> }

      <form onSubmit={onSubmitForm}>
        <DialogTitle>
          <Stack direction='row' spacing={2} alignItems='flex-start'>
            <Typography variant='h5' color='text.primary' flexGrow={1} overflow='hidden' textOverflow='ellipsis' whiteSpace='nowrap'>専門分野・関心領域の{data ? '編集' : '追加'}</Typography>
            <Tooltip title='閉じる'><IconButton onClick={onClose}><CloseIcon/></IconButton></Tooltip>
          </Stack>
        </DialogTitle>

        <DialogContent style={{ maxHeight: '80vh', overflow: 'auto' }}>
          <Stack direction='column'>
            <Section label= 'タイトル' labelWidth='5em' labelProps={{ paddingY: 2 }}>
              <FormControl><TextField hiddenLabel fullWidth variant='outlined' size='small' value={title} onChange={(event) => setTitle(event.target.value)}/></FormControl>
            </Section>

            <Section label= 'おすすめ度' labelWidth='5em' labelProps={{ paddingY: 2 }}>
              <Rating value={articleScore} max={17} onChange={(event, newValue) => setArticleScore(newValue as number)}/>
              <Typography variant='body2' color='text.secondary'>{articleScore}/17 点{articleScoreReason !== '' ? ` .. ${articleScoreReason}` : ''}</Typography>
            </Section>

            <Section label= '理由' labelWidth='5em' labelProps={{ paddingY: 2 }}>
              <FormControl><TextField hiddenLabel fullWidth variant='outlined' size='small' multiline rows={6} value={reason} onChange={(event) => setReason(event.target.value)}/></FormControl>
              <FormGroup style={{ flexDirection: 'row' }}>
                <FormControlLabel control={<Checkbox checked={ownFlag} onChange={(event) => setOwnFlag(event.target.checked)} />} label="自社製品" />
                <FormControlLabel control={<Checkbox checked={othersFlag} onChange={(event) => setOthersFlag(event.target.checked)} />} label="競合製品" />
                <FormControlLabel control={<Checkbox checked={priorityContentFlag} onChange={(event) => setPriorityContentFlag(event.target.checked)} />} label="承認外" />
              </FormGroup>
            </Section>

            <Section label= '参照論文' labelWidth='5em' labelProps={{ paddingY: 2 }}>
              <EditRefAreticleList articles={articles} refArticleIdList={refArticleIdList} setRefArticleIdList={setRefArticleIdList}/>
            </Section>
          </Stack>

          { postState.error && <ErrorInfo error={postState.error}/> }
          { editState.error && <ErrorInfo error={editState.error}/> }
          { deleteState.error && <ErrorInfo error={deleteState.error}/> }
        </DialogContent>

        <DialogActions style={{ padding: '1em' }}>
          { data && <Button variant="contained" color='error' size='large' startIcon={<TrushIcon/>} onClick={deleteLine}>削 除</Button> }
          <Box flexGrow={1}/>
          <Button variant="contained" size='large' type="submit" startIcon={<OkIcon/>}>実 行</Button>
          <Button variant="text" size='large' onClick={onClose}>キャンセル</Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}